import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/components/AuthContext";

export interface CurrencyInfo {
  code: string | null;
  symbol: string | null;
}

/**
 * Single source of truth for the farmer's currency: the `countries` row
 * (currency_code + currency_symbol), resolved at auth time from the farmer's
 * country_id and carried on the AuthContext user.
 *
 * There are deliberately NO hardcoded country/language → currency maps and NO
 * USD/BDT default here. Every country (current and future) renders its
 * MAP-configured symbol with zero code change. When the country row hasn't
 * resolved yet, `symbol`/`code` are null and consumers render a neutral state.
 */
export const useCountryCurrency = () => {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();

  const code = user?.currencyCode ?? null;
  const symbol = user?.currencySymbol ?? null;

  return {
    code,
    symbol,
    isReady: !!symbol,
    localizedName: code ? t(`currency.${code}`, { defaultValue: code }) : "",
    languageCode: i18n.language,
    countryCode: user?.countryCode ?? null,
  };
};

interface CurrencyIconProps {
  className?: string;
  size?: number;
}

/**
 * Renders the farmer's currency glyph (countries.currency_symbol) as an SVG
 * text node. Uses `currentColor` so Tailwind text-* classes at the call site
 * control the color, and a fixed viewBox so the glyph scales with `size`.
 * Renders a neutral, correctly-sized blank when no currency has resolved.
 */
export const CurrencyIcon: React.FC<CurrencyIconProps> = ({ className = "", size = 24 }) => {
  const { symbol } = useCountryCurrency();

  // fontSize is in viewBox units (24), so it scales uniformly with `size`.
  const fontSize = symbol && symbol.length > 1 ? 10 : 16;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      // B11 (axe svg-img-alt): an image needs a name; until the symbol is known the icon is decorative
      {...(symbol ? { role: "img", "aria-label": symbol } : { "aria-hidden": true })}
    >
      {symbol ? (
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          fill="currentColor"
          fontSize={fontSize}
          fontWeight="700"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {symbol}
        </text>
      ) : null}
    </svg>
  );
};

interface CountryCurrencyProps {
  className?: string;
  iconSize?: number;
  showName?: boolean;
  showIcon?: boolean;
  nameClassName?: string;
}

// Combined component showing icon and/or localized currency name.
export const CountryCurrency: React.FC<CountryCurrencyProps> = ({
  className = "",
  iconSize = 24,
  showName = true,
  showIcon = true,
  nameClassName = "",
}) => {
  const { localizedName } = useCountryCurrency();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showIcon && <CurrencyIcon size={iconSize} />}
      {showName && <span className={nameClassName}>{localizedName}</span>}
    </div>
  );
};
